import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge,
  Spinner, Alert, Form
} from 'react-bootstrap';
import { useClientAuth } from '../../context/ClientAuthContext';
import ClientNavbar from '../../components/ClientNavbar';
import clientService from '../../services/clientService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const EMPTY_FILTERS = {
  workOrderNo: '', certificateNo: '', equipmentName: '', year: '', month: '',
};

const ClientDashboard = () => {
  const { portalClient } = useClientAuth();
  const [clientName, setClientName] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const loadCertificates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clientService.listCertificates();
      if (data.success) {
        setCertificates(data.certificates);
        setClientName(data.client_name);
      } else {
        setError(data.message || 'Failed to load certificates');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCertificates(); }, [loadCertificates]);

  const handleDownload = async (submissionId, formNumber) => {
    setDownloadingId(submissionId);
    try {
      const response = await clientService.downloadCertificate(submissionId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formNumber || 'certificate'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB');
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const yearsAvailable = useMemo(() => {
    const years = new Set();
    certificates.forEach((c) => {
      if (c.approved_at) years.add(new Date(c.approved_at).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [certificates]);

  const filteredCertificates = useMemo(() => {
    const wo = filters.workOrderNo.trim().toLowerCase();
    const certNo = filters.certificateNo.trim().toLowerCase();
    const equip = filters.equipmentName.trim().toLowerCase();

    return certificates.filter((c) => {
      if (wo && !(c.work_order_number || '').toLowerCase().includes(wo)) return false;
      if (certNo && !(c.certificate_number || '').toLowerCase().includes(certNo)) return false;
      if (equip && !(c.equipment_name || '').toLowerCase().includes(equip)) return false;

      if (filters.year || filters.month) {
        if (!c.approved_at) return false;
        const d = new Date(c.approved_at);
        if (filters.year && d.getFullYear() !== Number(filters.year)) return false;
        if (filters.month && d.getMonth() + 1 !== Number(filters.month)) return false;
      }

      return true;
    });
  }, [certificates, filters]);

  const filtersActive = Object.values(filters).some((v) => v !== '');

  const { page, setPage, totalPages, paginatedItems: paginatedCertificates } = usePagination(filteredCertificates);

  useEffect(() => {
    setPage(1);
  }, [filters, setPage]);

  return (
    <>
      <ClientNavbar />

      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">
              <i className="fas fa-certificate text-primary me-2"></i>
              My Approved Certificates
            </h2>
            <p className="text-muted mb-0">{clientName || portalClient?.full_name}</p>
          </Col>
          <Col xs="auto">
            <Badge bg="success" className="fs-6 px-3 py-2">
              {filtersActive
                ? `${filteredCertificates.length} of ${certificates.length} Available`
                : `${certificates.length} Available`}
            </Badge>
          </Col>
        </Row>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {!loading && certificates.length > 0 && (
          <Card className="shadow-sm mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1 small text-muted">Work Order No</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search work order..."
                    value={filters.workOrderNo}
                    onChange={(e) => handleFilterChange('workOrderNo', e.target.value)}
                  />
                </Col>
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1 small text-muted">Certificate No</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search certificate..."
                    value={filters.certificateNo}
                    onChange={(e) => handleFilterChange('certificateNo', e.target.value)}
                  />
                </Col>
                <Col md={3} sm={6}>
                  <Form.Label className="mb-1 small text-muted">Equipment Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search equipment..."
                    value={filters.equipmentName}
                    onChange={(e) => handleFilterChange('equipmentName', e.target.value)}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="mb-1 small text-muted">Year</Form.Label>
                  <Form.Select
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                  >
                    <option value="">All Years</option>
                    {yearsAvailable.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2} sm={6}>
                  <Form.Label className="mb-1 small text-muted">Month</Form.Label>
                  <Form.Select
                    value={filters.month}
                    onChange={(e) => handleFilterChange('month', e.target.value)}
                  >
                    <option value="">All Months</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                {filtersActive && (
                  <Col xs="auto">
                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                      <i className="fas fa-times me-1"></i>
                      Clear Filters
                    </Button>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        )}

        <Card className="shadow-sm">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading certificates...</p>
              </div>
            ) : certificates.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-folder-open fa-3x mb-3"></i>
                <p>No certificates available yet.</p>
                <small>Certificates appear here once approved and payment is confirmed.</small>
              </div>
            ) : filteredCertificates.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-search fa-3x mb-3"></i>
                <p>No certificates match your filters.</p>
                <Button variant="link" size="sm" onClick={clearFilters}>Clear filters</Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr style={{ '--bs-table-bg': '#146c43', '--bs-table-color': '#fff' }}>
                      <th>Form Number</th>
                      <th>Work Order No</th>
                      <th>Certificate Number</th>
                      <th>Equipment</th>
                      <th>Approved At</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCertificates.map((cert) => (
                      <tr key={cert.submission_id}>
                        <td className="fw-bold">{cert.form_number}</td>
                        <td>{cert.work_order_number || '—'}</td>
                        <td>{cert.certificate_number || '—'}</td>
                        <td>{cert.equipment_name || '—'}</td>
                        <td>{formatDate(cert.approved_at)}</td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={downloadingId === cert.submission_id}
                            onClick={() => handleDownload(cert.submission_id, cert.form_number)}
                          >
                            {downloadingId === cert.submission_id ? (
                              <span className="spinner-border spinner-border-sm me-1"></span>
                            ) : (
                              <i className="fas fa-download me-1"></i>
                            )}
                            Download PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ClientDashboard;
